import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Users, 
  Stethoscope, 
  Calendar, 
  TrendingUp, 
  Building2, 
  Ear,
  ArrowRight,
  Activity,
  Clock,
  Filter
} from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user, isSuperAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState("all");

  useEffect(() => {
    fetchClinics();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedClinic]);

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get("/clinics");
      setClinics(response.data);
    } catch (error) {
      console.error("Failed to fetch clinics:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const params = selectedClinic !== "all" ? { clinic_id: selectedClinic } : {};
      const response = await apiClient.get("/analytics/overview", { params });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      // If no data, try seeding
      if (error.response?.status === 500 || !stats) {
        await seedData();
      }
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    setSeeding(true);
    try {
      const response = await apiClient.post("/seed-data");
      toast.success("Demo data loaded successfully!");
      console.log("Seed result:", response.data);
      await fetchStats();
    } catch (error) {
      if (error.response?.data?.message !== "Data already seeded") {
        console.error("Seed failed:", error);
      }
    } finally {
      setSeeding(false);
    }
  };

  const statCards = [
    {
      title: "Total Patients",
      value: stats?.total_patients || 0,
      icon: Users,
      color: "bg-sky-500",
      link: "/patients"
    },
    {
      title: "Hearing Tests",
      value: stats?.total_tests || 0,
      icon: Ear,
      color: "bg-emerald-500",
      link: "/pta-test"
    },
    {
      title: "Recommendations",
      value: stats?.total_recommendations || 0,
      icon: Stethoscope,
      color: "bg-violet-500",
      link: "/recommendations"
    },
    {
      title: "Appointments",
      value: stats?.total_appointments || 0,
      icon: Calendar,
      color: "bg-amber-500",
      link: "/appointments"
    }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
              Welcome, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-slate-500 mt-1">
              {isSuperAdmin ? "Super Admin Dashboard" : "Clinic Admin Dashboard"} • {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex gap-3 items-center">
            {/* Clinic Filter for Super Admin */}
            {isSuperAdmin && (
              <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                <SelectTrigger className="w-[220px]" data-testid="clinic-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Clinics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clinics ({clinics.length})</SelectItem>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.id} value={clinic.id}>
                      {clinic.name.replace("HearClear ", "")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button asChild className="bg-sky-600 hover:bg-sky-700">
              <Link to="/patients" data-testid="new-patient-btn">
                <Users className="w-4 h-4 mr-2" />
                View Patients
              </Link>
            </Button>
            {(seeding || loading) && stats?.total_patients === 0 && (
              <Button variant="outline" onClick={seedData} disabled={seeding}>
                {seeding ? "Loading Demo..." : "Load Demo Data"}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => (
            <Link to={stat.link} key={idx}>
              <Card className="stat-card card-hover cursor-pointer" data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">
                        {loading ? (
                          <span className="skeleton w-16 h-8 block"></span>
                        ) : (
                          stat.value.toLocaleString()
                        )}
                      </p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-xl`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5 text-sky-600" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/patients">
                  <Users className="w-4 h-4 mr-3 text-sky-600" />
                  View All Patients
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/pta-test">
                  <Ear className="w-4 h-4 mr-3 text-emerald-600" />
                  New Hearing Test
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/catalog">
                  <Stethoscope className="w-4 h-4 mr-3 text-violet-600" />
                  Browse Catalog
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/appointments">
                  <Calendar className="w-4 h-4 mr-3 text-amber-600" />
                  View Appointments
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Link>
              </Button>
              {isSuperAdmin && (
                <Button asChild variant="outline" className="w-full justify-start">
                  <Link to="/clinics">
                    <Building2 className="w-4 h-4 mr-3 text-rose-600" />
                    Manage Clinics
                    <ArrowRight className="w-4 h-4 ml-auto" />
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Recent Tests */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-sky-600" />
                Recent Hearing Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-16 w-full"></div>
                  ))}
                </div>
              ) : stats?.recent_tests?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_tests.map((test, idx) => (
                    <div 
                      key={test.id || idx} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                          <Ear className="w-5 h-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">PTA Test</p>
                          <p className="text-sm text-slate-500">
                            L: {test.pta_left?.toFixed(1) || "N/A"} dB • R: {test.pta_right?.toFixed(1) || "N/A"} dB
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          test.severity_left === "Normal" ? "bg-emerald-100 text-emerald-700" :
                          test.severity_left === "Mild" ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {test.severity_left || "Pending"}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(test.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Ear className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No recent tests</p>
                  <Button asChild variant="link" className="mt-2">
                    <Link to="/pta-test">Create your first test</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Clinic Stats (Super Admin Only) */}
        {isSuperAdmin && stats?.clinic_stats?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-600" />
                Clinic Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="text-left p-3">Clinic</th>
                      <th className="text-left p-3">City</th>
                      <th className="text-right p-3">Patients</th>
                      <th className="text-right p-3">Tests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.clinic_stats.slice(0, 5).map((clinic, idx) => (
                      <tr key={clinic.clinic_id || idx} className="table-row border-b border-slate-100">
                        <td className="p-3 font-medium text-slate-900">{clinic.clinic_name}</td>
                        <td className="p-3 text-slate-600">{clinic.city}</td>
                        <td className="p-3 text-right text-slate-900">{clinic.patients}</td>
                        <td className="p-3 text-right text-slate-900">{clinic.tests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
